import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Skeleton, SkeletonCard } from './Skeleton';
import {
    GraduationCap,
    PanelLeft,
    BookOpen,
    BarChart3,
    User,
    Clock,
    Building2,
    ClipboardList,
    DollarSign,
    Hammer,
    CheckCircle2,
    Zap,
    Users,
    FileQuestion,
    ArrowLeft,
    ArrowRight,
} from 'lucide-react';
import {
    getCourses,
    getLessons,
    getQuizzes,
    type Course as APICourse,
    type Lesson as APILesson,
    type Quiz as APIQuiz,
} from '../courseService';
import {
    fetchCurrentUserWithPlan,
    hasMentorshipRole,
    type BackendUserWithPlan,
} from '../userService';
import Grid from './ui/Grid';
import Button from './ui/Button';
import ProgressBar from './ui/ProgressBar';
import CourseCard from './education/CourseCard';
import EducationStatsStrip from './education/EducationStatsStrip';
import LessonsPanel from './education/LessonsPanel';
import QuizFlow, { type Quiz as QuizFlowQuiz } from './education/QuizFlow';
import BootcampView from './education/BootcampView';
import MentorshipView from './mentorship/MentorshipView';
import './education/CourseLayout.css';
import './EducationView.css';

type EducationType = 'bootcamp' | 'coaching';

type Lesson = {
    id: string;
    title: string;
    duration: string;
    content: string;
    videoUrl?: string;
    completed: boolean;
    hasQuiz?: boolean;
};

type Course = {
    id: string;
    title: string;
    description: string;
    instructor: string;
    duration: string;
    lessonsCount: number;
    progress: number;
    level: 'Beginner' | 'Intermediate' | 'Advanced';
    category: string;
    thumbnail: string;
    lessons: Lesson[];
    quizId?: string;
};

type EducationViewProps = {
    onMenuClick: () => void;
    showMenu: boolean;
    initialSubTab?: string;
};

function getThumbnailIcon(thumbnail: string): ReactNode {
    switch (thumbnail) {
        case 'building':
            return <Building2 size={32} />;
        case 'clipboard':
            return <ClipboardList size={32} />;
        case 'dollar':
            return <DollarSign size={32} />;
        case 'hammer':
            return <Hammer size={32} />;
        default:
            return <BookOpen size={32} />;
    }
}

function apiCourseToLocal(apiCourse: APICourse): Course {
    return {
        id: apiCourse.id,
        title: apiCourse.title,
        description: apiCourse.description,
        instructor: apiCourse.instructor,
        duration: apiCourse.duration,
        lessonsCount: apiCourse.lessons_count,
        progress: 0, // Progress tracking not implemented in backend yet
        level: apiCourse.level,
        category: apiCourse.category,
        thumbnail: apiCourse.thumbnail,
        lessons: [],
    };
}

function apiLessonToLocal(apiLesson: APILesson): Lesson {
    return {
        id: apiLesson.id,
        title: apiLesson.title,
        duration: apiLesson.duration,
        content: apiLesson.content,
        videoUrl: apiLesson.video_url || undefined,
        completed: false,
        hasQuiz: apiLesson.has_quiz,
    };
}

function apiQuizToLocal(apiQuiz: APIQuiz): QuizFlowQuiz {
    return {
        id: apiQuiz.id,
        title: apiQuiz.title,
        description: apiQuiz.description,
        courseId: apiQuiz.course_id,
        difficulty: apiQuiz.difficulty,
        timeLimit: apiQuiz.time_limit,
        passingScore: apiQuiz.passing_score,
        questions: apiQuiz.questions.map((q) => ({
            id: q.id,
            type: q.type,
            question: q.question,
            options: q.options,
            correctAnswer: q.correct_answer,
            explanation: q.explanation,
        })),
    };
}

/** Render the lightweight markdown used in lesson content:
 *  **heading**, - bullet, 1. numbered, blank line = break.
 *  Consecutive list items are grouped into a real <ul>/<ol>. */
function renderLessonMarkup(content: string): ReactNode {
    const lines = content.split('\n');
    const out: ReactNode[] = [];
    let bulletBuf: string[] = [];
    let numberedBuf: string[] = [];

    const flushBullets = () => {
        if (bulletBuf.length === 0) return;
        out.push(
            <ul key={`ul-${out.length}`} className="content-list">
                {bulletBuf.map((item, i) => (
                    <li key={i} className="content-list-item">
                        {item}
                    </li>
                ))}
            </ul>,
        );
        bulletBuf = [];
    };

    const flushNumbered = () => {
        if (numberedBuf.length === 0) return;
        out.push(
            <ol key={`ol-${out.length}`} className="content-list numbered">
                {numberedBuf.map((item, i) => (
                    <li key={i} className="content-list-item">
                        {item}
                    </li>
                ))}
            </ol>,
        );
        numberedBuf = [];
    };

    lines.forEach((raw, idx) => {
        const line = raw;
        if (line.startsWith('**') && line.endsWith('**')) {
            flushBullets();
            flushNumbered();
            out.push(
                <h4 key={idx} className="content-heading">
                    {line.slice(2, -2)}
                </h4>,
            );
            return;
        }
        if (line.startsWith('- ')) {
            flushNumbered();
            bulletBuf.push(line.slice(2));
            return;
        }
        if (/^\d+\.\s/.test(line)) {
            flushBullets();
            numberedBuf.push(line.replace(/^\d+\.\s/, ''));
            return;
        }
        flushBullets();
        flushNumbered();
        if (line.trim() === '') {
            out.push(<br key={idx} />);
            return;
        }
        out.push(
            <p key={idx} className="content-paragraph">
                {line}
            </p>,
        );
    });

    flushBullets();
    flushNumbered();
    return out;
}

function EducationView({ onMenuClick, showMenu, initialSubTab = 'bootcamp' }: EducationViewProps) {
    const contentRef = useRef<HTMLDivElement>(null);
    const [educationType, setEducationType] = useState<EducationType>(
        initialSubTab as EducationType,
    );
    const [activeSection, setActiveSection] = useState<'courses' | 'progress'>('courses');
    const [activeCourse, setActiveCourse] = useState<Course | null>(null);
    const [activeLesson, setActiveLesson] = useState<Lesson | null>(null);
    const [activeQuiz, setActiveQuiz] = useState<QuizFlowQuiz | null>(null);
    const [bootcampCourses, setBootcampCourses] = useState<Course[]>([]);
    const [coachingCourses, setCoachingCourses] = useState<Course[]>([]);
    const [loadingCourses, setLoadingCourses] = useState(true);
    const [loadingLessons, setLoadingLessons] = useState(false);
    const [quizzes, setQuizzes] = useState<QuizFlowQuiz[]>([]);

    // Mentorship integration: fetch the current user once so the coaching
    // sub-tab can decide whether to swap in <MentorshipView /> (if the user
    // has a role) or fall through to the legacy course list (role===null).
    // `currentUserStatus` gates the coaching branch render so we don't flash
    // the legacy course list before the user fetch resolves.
    const [currentUser, setCurrentUser] = useState<BackendUserWithPlan | null>(null);
    const [currentUserStatus, setCurrentUserStatus] = useState<
        'idle' | 'loading' | 'ready' | 'error'
    >('loading');
    const [showMentorshipOptIn, setShowMentorshipOptIn] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setCurrentUserStatus('loading');
            try {
                const u = await fetchCurrentUserWithPlan();
                if (cancelled) return;
                setCurrentUser(u);
                setCurrentUserStatus(u ? 'ready' : 'error');
            } catch {
                if (cancelled) return;
                setCurrentUser(null);
                setCurrentUserStatus('error');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Keep active bootcamp/coaching in sync with parent navigation
    useEffect(() => {
        setEducationType(initialSubTab as EducationType);
    }, [initialSubTab]);

    // Fetch courses
    useEffect(() => {
        let cancelled = false;
        async function fetchCourses() {
            try {
                const [bootcampRes, coachingRes] = await Promise.all([
                    getCourses('bootcamp'),
                    getCourses('coaching'),
                ]);
                if (cancelled) return;
                setBootcampCourses(bootcampRes.courses.map(apiCourseToLocal));
                setCoachingCourses(coachingRes.courses.map(apiCourseToLocal));
            } catch (error) {
                console.error('Failed to load courses:', error);
                if (!cancelled) {
                    setBootcampCourses([]);
                    setCoachingCourses([]);
                }
            } finally {
                if (!cancelled) setLoadingCourses(false);
            }
        }
        fetchCourses();
        return () => {
            cancelled = true;
        };
    }, []);

    // Fetch quizzes for all courses after courses are loaded
    useEffect(() => {
        const allCourses = [...bootcampCourses, ...coachingCourses];
        if (allCourses.length === 0) return;

        let cancelled = false;
        async function fetchQuizzes() {
            try {
                const quizPromises = allCourses.map((course) =>
                    getQuizzes(course.id).catch(() => [] as APIQuiz[]),
                );
                const results = await Promise.all(quizPromises);
                if (cancelled) return;
                const allQuizzes = results.flat().map(apiQuizToLocal);
                setQuizzes(allQuizzes);
            } catch (error) {
                console.error('Failed to load quizzes:', error);
                if (!cancelled) setQuizzes([]);
            }
        }
        fetchQuizzes();
        return () => {
            cancelled = true;
        };
    }, [bootcampCourses, coachingCourses]);

    // Active list derived from educationType
    const activeList = educationType === 'bootcamp' ? bootcampCourses : coachingCourses;
    const inProgressCourses = useMemo(
        () => activeList.filter((c) => c.progress > 0 && c.progress < 100),
        [activeList],
    );
    const completedCourses = useMemo(
        () => activeList.filter((c) => c.progress === 100),
        [activeList],
    );

    // Course content handlers
    const openCourse = useCallback(async (course: Course) => {
        setActiveCourse(course);
        setActiveQuiz(null);
        setLoadingLessons(true);
        try {
            const apiLessons = await getLessons(course.id);
            const lessons = apiLessons.map(apiLessonToLocal);
            const updatedCourse = { ...course, lessons };
            setActiveCourse(updatedCourse);
            const firstIncomplete = lessons.find((l) => !l.completed);
            setActiveLesson(firstIncomplete || lessons[0] || null);
        } catch (error) {
            console.error('Failed to load lessons:', error);
            setActiveLesson(null);
        } finally {
            setLoadingLessons(false);
        }
    }, []);

    const closeCourse = useCallback(() => {
        setActiveCourse(null);
        setActiveLesson(null);
        setActiveQuiz(null);
    }, []);

    const selectLesson = useCallback((lessonId: string) => {
        setActiveCourse((curr) => {
            if (!curr) return curr;
            const lesson = curr.lessons.find((l) => l.id === lessonId) || null;
            setActiveLesson(lesson);
            setActiveQuiz(null);
            const lessonBody = document.querySelector('.lesson-content-body');
            const contentEl = document.querySelector('.education-content');
            if (lessonBody) lessonBody.scrollTop = 0;
            if (contentEl) contentEl.scrollTop = 0;
            return curr;
        });
    }, []);

    const startLessonQuiz = useCallback(() => {
        if (activeCourse?.quizId) {
            const quiz = quizzes.find((q) => q.id === activeCourse.quizId);
            if (quiz) setActiveQuiz(quiz);
        }
    }, [activeCourse, quizzes]);

    const exitQuiz = useCallback(() => {
        setActiveQuiz(null);
    }, []);

    const currentLessonIdx = activeCourse && activeLesson
        ? activeCourse.lessons.findIndex((l) => l.id === activeLesson.id)
        : -1;

    // ── Render branches ──────────────────────────────────────────────

    const renderCourseList = () => (
        <div className="courses-section">
            {loadingCourses ? (
                <Grid minItemWidth={280} gap="lg">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <SkeletonCard key={i} />
                    ))}
                </Grid>
            ) : activeList.length === 0 ? (
                <div className="education-empty">
                    <div className="empty-state">
                        <div className="empty-icon">
                            <BookOpen size={48} />
                        </div>
                        <h2>No courses yet</h2>
                        <p>
                            {educationType === 'bootcamp'
                                ? 'Your bootcamp catalogue will appear here once courses are available.'
                                : 'Your coaching catalogue will appear here once courses are available.'}
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    {inProgressCourses.length > 0 && (
                        <>
                            <h2 className="section-title">Continue learning</h2>
                            <Grid minItemWidth={280} gap="lg">
                                {inProgressCourses.map((course) => (
                                    <CourseCard
                                        key={course.id}
                                        course={course}
                                        progress={course.progress}
                                        thumbnailIcon={getThumbnailIcon(course.thumbnail)}
                                        onOpen={() => openCourse(course)}
                                    />
                                ))}
                            </Grid>
                        </>
                    )}

                    <h2 className="section-title">All courses</h2>
                    <Grid minItemWidth={280} gap="lg">
                        {activeList.map((course) => (
                            <CourseCard
                                key={course.id}
                                course={course}
                                progress={course.progress}
                                thumbnailIcon={getThumbnailIcon(course.thumbnail)}
                                onOpen={() => openCourse(course)}
                            />
                        ))}
                    </Grid>
                </>
            )}
        </div>
    );

    const renderProgress = () => (
        <div className="progress-section">
            <EducationStatsStrip
                total={activeList.length}
                completed={completedCourses.length}
                inProgress={inProgressCourses.length}
            />

            {activeList.length > 0 && (
                <>
                    <h2 className="section-title">Course progress</h2>
                    <div className="progress-list">
                        {activeList.map((course) => (
                            <div key={course.id} className="progress-item">
                                <div className="progress-item-info">
                                    <span className="progress-thumbnail">
                                        {getThumbnailIcon(course.thumbnail)}
                                    </span>
                                    <div className="progress-details">
                                        <span className="progress-title">{course.title}</span>
                                        <span className="progress-meta">
                                            {course.lessonsCount} lessons · {course.duration}
                                        </span>
                                    </div>
                                </div>
                                <div className="progress-item-bar">
                                    <ProgressBar
                                        value={course.progress}
                                        showLabel
                                        ariaLabel={`${course.title} progress`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );

    const renderLessonContent = () => {
        if (loadingLessons) {
            return (
                <div className="lesson-content-body lesson-content-body--loading">
                    <Skeleton width="60%" height="24px" />
                    <Skeleton width="100%" height="14px" />
                    <Skeleton width="95%" height="14px" />
                    <Skeleton width="90%" height="14px" />
                </div>
            );
        }

        if (!activeLesson) {
            return (
                <div className="empty-state">
                    <div className="empty-icon">
                        <BookOpen size={48} />
                    </div>
                    <h2>Select a lesson</h2>
                    <p>Choose a lesson to begin learning</p>
                </div>
            );
        }

        return (
            <>
                <div className="lesson-content-header">
                    <h3 className="lesson-content-title">{activeLesson.title}</h3>
                    <div className="lesson-content-meta">
                        <span className="lesson-duration">
                            <Clock size={14} />
                            {activeLesson.duration}
                        </span>
                        {activeLesson.completed && (
                            <span className="completed-badge">
                                <CheckCircle2 size={14} />
                                Completed
                            </span>
                        )}
                    </div>
                </div>
                <div className="lesson-content-body">
                    {renderLessonMarkup(activeLesson.content)}

                    {activeLesson.hasQuiz && activeCourse?.quizId && (
                        <div className="lesson-quiz-cta">
                            <div className="quiz-cta-content">
                                <FileQuestion size={24} />
                                <div className="quiz-cta-text">
                                    <h4>Ready to test your knowledge?</h4>
                                    <p>
                                        Take the quiz to assess what you've learned in this
                                        section.
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="primary"
                                size="md"
                                icon={<ArrowRight size={18} />}
                                iconPosition="right"
                                onClick={startLessonQuiz}
                            >
                                Start quiz
                            </Button>
                        </div>
                    )}

                    {activeCourse && activeCourse.lessons.length > 1 && (
                        <div className="lesson-navigation">
                            {currentLessonIdx > 0 && (
                                <Button
                                    variant="secondary"
                                    size="md"
                                    icon={<ArrowLeft size={18} />}
                                    onClick={() =>
                                        selectLesson(
                                            activeCourse.lessons[currentLessonIdx - 1].id,
                                        )
                                    }
                                >
                                    Previous lesson
                                </Button>
                            )}
                            {currentLessonIdx >= 0 &&
                                currentLessonIdx < activeCourse.lessons.length - 1 && (
                                    <Button
                                        variant="primary"
                                        size="md"
                                        icon={<ArrowRight size={18} />}
                                        iconPosition="right"
                                        onClick={() =>
                                            selectLesson(
                                                activeCourse.lessons[currentLessonIdx + 1].id,
                                            )
                                        }
                                    >
                                        Next lesson
                                    </Button>
                                )}
                        </div>
                    )}
                </div>
            </>
        );
    };

    const renderCourseDetail = () => {
        if (!activeCourse) return null;
        return (
            <div className="course-content-view">
                <div className="course-content-header">
                    <Button
                        variant="ghost"
                        size="md"
                        icon={<ArrowLeft size={18} />}
                        onClick={closeCourse}
                    >
                        Back to courses
                    </Button>
                    <div className="course-header-info">
                        <div className="course-header-meta">
                            <span
                                className={`course-card__level course-card__level--${activeCourse.level.toLowerCase()}`}
                            >
                                {activeCourse.level}
                            </span>
                            <span className="course-card__category">{activeCourse.category}</span>
                        </div>
                        <h2 className="course-header-title">{activeCourse.title}</h2>
                        <p className="course-header-instructor">
                            <User size={14} />
                            {activeCourse.instructor}
                        </p>
                    </div>
                </div>

                {activeQuiz ? (
                    <QuizFlow quiz={activeQuiz} onExit={exitQuiz} />
                ) : (
                    <div className="course-layout">
                        <div className="course-layout__sidebar">
                            <LessonsPanel
                                lessons={activeCourse.lessons}
                                activeLessonId={activeLesson?.id ?? null}
                                loading={loadingLessons}
                                onSelect={selectLesson}
                            />
                        </div>
                        <div className="course-layout__content">
                            <div className="lesson-content">{renderLessonContent()}</div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="education-view">
            {/* Header */}
            <header
                className="education-header"
                onClick={() => contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
            >
                {showMenu && (
                    <button
                        type="button"
                        className="menu-btn"
                        onClick={onMenuClick}
                        aria-label="Open menu"
                    >
                        <PanelLeft size={20} />
                    </button>
                )}
                <div className="header-content">
                    <span className="header-icon">
                        <GraduationCap size={24} />
                    </span>
                    <h1 className="header-title">Education</h1>
                </div>
            </header>

            {/* Bootcamp / Coaching toggle — mobile only (desktop uses sidebar) */}
            {!activeCourse && (
                <div className="education-type-toggle mobile-only">
                    <div className="education-type-toggle-inner">
                        <button
                            type="button"
                            className={`type-toggle-btn ${educationType === 'bootcamp' ? 'active' : ''}`}
                            onClick={() => setEducationType('bootcamp')}
                        >
                            <Zap size={18} />
                            <span>Bootcamp</span>
                        </button>
                        <button
                            type="button"
                            className={`type-toggle-btn ${educationType === 'coaching' ? 'active' : ''}`}
                            onClick={() => setEducationType('coaching')}
                        >
                            <Users size={18} />
                            <span>Coaching</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Section tabs — hidden while viewing a course */}
            {!activeCourse && (
                <div className="section-tabs">
                    <button
                        type="button"
                        className={`section-tab ${activeSection === 'courses' ? 'active' : ''}`}
                        onClick={() => setActiveSection('courses')}
                    >
                        <BookOpen size={18} />
                        Courses
                    </button>
                    <button
                        type="button"
                        className={`section-tab ${activeSection === 'progress' ? 'active' : ''}`}
                        onClick={() => setActiveSection('progress')}
                    >
                        <BarChart3 size={18} />
                        Progress
                    </button>
                </div>
            )}

            {/* Content area */}
            <div className="education-content" ref={contentRef}>
                {activeCourse
                    ? renderCourseDetail()
                    : activeSection === 'courses'
                    ? renderCoursesBranch()
                    : renderProgress()}
            </div>
        </div>
    );

    /**
     * Coaching sub-tab takeover branch.
     *
     * - Bootcamp: always renders the course list (no user fetch dependency).
     * - Coaching + user fetch in flight: renders a skeleton placeholder so
     *   we don't flash the legacy course list with banner before resolving.
     * - Coaching + user fetch errored: renders the legacy course list as a
     *   safe fallback (no banner — we can't tell whether they have a role).
     * - Coaching + user has any mentorship role (mentor/mentee/admin): renders
     *   <MentorshipView />, replacing the legacy coaching course list.
     * - Coaching + role===null + user has clicked "Apply": also renders
     *   <MentorshipView /> (which shows the RoleOnboardingSplash).
     * - Coaching + role===null + user has NOT opted in: renders the legacy
     *   course list with a "Want to join the mentorship program?" banner
     *   above. Existing mentee-less users see no behavioral change.
     */
    function renderCoursesBranch() {
        if (educationType === 'bootcamp') {
            // New program-first timeline replaces the flat course-grid.
            // Keeps the sub-tab stable while surfacing enrollment + weekly
            // progression.
            return <BootcampView />;
        }
        if (educationType !== 'coaching') {
            return renderCourseList();
        }
        if (currentUserStatus === 'loading') {
            // Don't flash any UI until the user fetch resolves — prevents
            // a brief render of the legacy course list for users who turn
            // out to have a mentorship role.
            return (
                <Grid minItemWidth={280} gap="lg">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <SkeletonCard key={i} />
                    ))}
                </Grid>
            );
        }
        if (currentUserStatus === 'error') {
            // Couldn't fetch the user — fall back to the legacy course list
            // without the banner. Better than blocking the user entirely.
            return renderCourseList();
        }
        const userHasRole = hasMentorshipRole(currentUser);
        if (userHasRole || showMentorshipOptIn) {
            return <MentorshipView initialUser={currentUser} />;
        }
        return (
            <>
                <div className="mentorship-banner">
                    <Users size={20} className="mentorship-banner__icon" />
                    <p className="mentorship-banner__text">
                        Want structured coaching? Join the mentorship program to get
                        quarterly framework reviews from a senior mentor.
                    </p>
                    <Button
                        variant="primary"
                        size="md"
                        className="mentorship-banner__cta"
                        onClick={() => setShowMentorshipOptIn(true)}
                    >
                        Apply
                    </Button>
                </div>
                {renderCourseList()}
            </>
        );
    }
}

export default EducationView;
